/**
 * PURPOSE: Recibir el estado local de HandballBeach en la pantalla externa del scoreboard.
 * RESPONSIBILITIES:
 *  - Sí: pedir estado actual al montar y consumir SYNC_MATCH/STATE_UPDATE.
 *  - Sí: escuchar REQUEST_FULLSCREEN y pedir Fullscreen API con fallback visual si el navegador lo bloquea.
 *  - Sí: exponer estado de conexión simple para UI.
 *  - No: no llama backend, no usa Redux y no calcula reglas deportivas.
 * COLLABORATORS:
 *  - constants/handballBeachScoreboardChannel.js
 *  - components/scoreboard/HandballBeachScoreboardView.jsx
 * NOTES: La pestaña TV depende de que la planilla esté abierta en el mismo origen/navegador.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/HandballBeach/hooks/useHandballBeachScoreboardReceiver.js
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  HANDBALL_BEACH_SCOREBOARD_CHANNEL,
  HANDBALL_BEACH_SCOREBOARD_MESSAGE,
} from "../constants/handballBeachScoreboardChannel.js";
import {
  HANDBALL_BEACH_SCOREBOARD_ZOOM,
  clampHandballBeachScoreboardZoom,
  readHandballBeachScoreboardZoom,
  stepHandballBeachScoreboardZoom,
  writeHandballBeachScoreboardZoom,
} from "../utils/handballBeachScoreboardZoomStorage.js";

// Valida lo mínimo para no pintar payloads corruptos.
function isValidScoreboardState(payload) {
  return Boolean(payload?.idmatch && payload?.homeTeam && payload?.awayTeam && payload?.clock && payload?.score);
}

// Hook consumidor: la pantalla TV recibe snapshots desde la planilla.
export function useHandballBeachScoreboardReceiver() {
  const supported = useMemo(
    () => typeof window !== "undefined" && typeof window.BroadcastChannel === "function",
    []
  );
  const [state, setState] = useState(null);
  const [status, setStatus] = useState(supported ? "waiting" : "unsupported");
  const [lastReceivedAt, setLastReceivedAt] = useState(null);
  const [fullscreenPrompt, setFullscreenPrompt] = useState(false);
  const [scoreboardZoom, setScoreboardZoom] = useState(() => readHandballBeachScoreboardZoom());

  // Fullscreen puede ser bloqueado si el navegador exige gesto directo dentro de la ventana TV.
  const requestFullscreen = useCallback(async () => {
    if (typeof document === "undefined") return false;

    try {
      if (document.fullscreenElement) {
        setFullscreenPrompt(false);
        return true;
      }

      await document.documentElement?.requestFullscreen?.();
      setFullscreenPrompt(false);
      return true;
    } catch (err) {
      setFullscreenPrompt(true);
      return false;
    }
  }, []);

  const clearFullscreenPrompt = useCallback(() => {
    setFullscreenPrompt(false);
  }, []);

  // Aplica y persiste zoom visual. No usa zoom del navegador ni modifica la planilla.
  const applyScoreboardZoom = useCallback((nextZoom) => {
    const normalized = writeHandballBeachScoreboardZoom(nextZoom);
    setScoreboardZoom(normalized);
    return normalized;
  }, []);

  // Evita que mensajes viejos/de otro partido ajusten una TV que ya recibió otro match.
  const isSameMatchOrNoStateYet = useCallback((payload) => {
    if (!payload?.idmatch || !state?.idmatch) return true;
    return String(payload.idmatch) === String(state.idmatch);
  }, [state?.idmatch]);

  useEffect(() => {
    if (!supported) return undefined;

    const channel = new BroadcastChannel(HANDBALL_BEACH_SCOREBOARD_CHANNEL);

    channel.onmessage = (event) => {
      const { type, payload } = event.data || {};

      if (
        type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.SYNC_MATCH ||
        type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.STATE_UPDATE
      ) {
        if (!isValidScoreboardState(payload)) return;
        setState((prev) => {
          if (!prev || String(prev.idmatch) !== String(payload.idmatch)) return payload;
          return { ...prev, ...payload };
        });
        setLastReceivedAt(Date.now());
        setStatus("connected");
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.REQUEST_FULLSCREEN) {
        requestFullscreen();
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.SET_ZOOM) {
        if (!isSameMatchOrNoStateYet(payload)) return;
        applyScoreboardZoom(clampHandballBeachScoreboardZoom(payload?.zoom));
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.ZOOM_IN) {
        if (!isSameMatchOrNoStateYet(payload)) return;
        applyScoreboardZoom(stepHandballBeachScoreboardZoom(scoreboardZoom, "in"));
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.ZOOM_OUT) {
        if (!isSameMatchOrNoStateYet(payload)) return;
        applyScoreboardZoom(stepHandballBeachScoreboardZoom(scoreboardZoom, "out"));
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.RESET_ZOOM) {
        if (!isSameMatchOrNoStateYet(payload)) return;
        applyScoreboardZoom(HANDBALL_BEACH_SCOREBOARD_ZOOM.DEFAULT);
        return;
      }

      if (type === HANDBALL_BEACH_SCOREBOARD_MESSAGE.PLANILLA_CLOSED) {
        setStatus("planilla_closed");
      }
    };

    channel.postMessage({
      source: "winscore-handballBeach-scoreboard",
      type: HANDBALL_BEACH_SCOREBOARD_MESSAGE.REQUEST_CURRENT_STATE,
      payload: { requestedAt: Date.now() },
    });

    return () => {
      channel.close();
    };
  }, [supported, requestFullscreen, applyScoreboardZoom, isSameMatchOrNoStateYet, scoreboardZoom]);

  return {
    state,
    status,
    supported,
    lastReceivedAt,
    fullscreenPrompt,
    scoreboardZoom,
    requestFullscreen,
    clearFullscreenPrompt,
  };
}
